import * as React from 'react';
import { useContext } from 'react';

// Utils
import { searchHooks } from '@src/services';
import { join, map, pipe } from '@src/shared/src/util/general';
import { useTranslation } from 'react-i18next';
import { AppContext } from '@toolkit/util/AppContext';
import { getUserFullname } from '@src/shared/src/util/users';
import useClickOutside from '@toolkit/util/useClickOutside';
// Constants
import { SEARCH_TYPE } from '@src/shared/src/const/app';
import { API_URL } from '@src/shared/src/const/api';
// Actions
// Models
import { PassengerModel } from '@src/shared/src/models';
// Interfaces
// Components
import { Link, TextField } from '@toolkit/ui';
import SearchBarSlideOutNavbar from '@src/pod/search/components/SearchBarSlideOutNavbar';
import {
  SearchBarGuestCreator,
  SearchBarPassengersSelector,
  SearchBarSlideOut,
} from '@pod/search/components';
import { AccountCirlceIcon } from '@toolkit/ui/icons';
// Styles
import '../styles/SearchBarPassengers.scss';

type Props = {
  isCurrentUserAdmin: boolean;
  isCurrentUserTravelAssistant: boolean;
  isCurrentUserManager: boolean;
  searchPassengers: PassengerModel[];
  organizationId: number;
  disabled?: boolean;
};

const SearchBarPassengers: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const appContext = useContext(AppContext);
  const [slideOutVisible, setSlideOutVisible] = React.useState(false);
  const outsideRef = useClickOutside(() => setSlideOutVisible(false));
  const [searchType] = searchHooks.useSearchState<SEARCH_TYPE>(['currentSearch', 'searchType']);
  const isPassengersMissing = searchHooks.useIsPassengersMissing();
  const isPassengersSelectionWrong = searchHooks.useIsPassengersSelectionWrong();
  const isRentalDriverCountWrong = searchHooks.useIsRentalDriverCountWrong();
  const isPassengersMoreThanAllowed = searchHooks.useIsPassengersMoreThanAllowed();

  const getErrorMsg = () => {
    if (isPassengersMissing) return t('searchBarConn.notify.text.removeFirstPassenger');
    if (isPassengersSelectionWrong) return t('searchBarConn.notify.text.removeYourself');
    if (isRentalDriverCountWrong) return t('searchBarConn.notify.text.rentalDriverCount');
    if (isPassengersMoreThanAllowed) return t('search.bar.passengers.maxPassenger.error');
  };

  return (
    <div ref={outsideRef} className="tcp-search-bar-passengers">
      <TextField
        name="passengers"
        label={
          searchType === SEARCH_TYPE.RENTAL
            ? t('searchBarPassengers.title.rental')
            : t('search.bar.passengers.title.travellers')
        }
        value={pipe(map(getUserFullname), join(' | '))(props.searchPassengers)}
        icon={<AccountCirlceIcon />}
        errorMsg={getErrorMsg()}
        onFocus={() => setSlideOutVisible(true)}
        disabled={props.disabled}
      />
      <SearchBarSlideOut isOpen={slideOutVisible} className="is--passenger">
        {!appContext.isMediaBPNotMobile ? (
          <SearchBarSlideOutNavbar onClose={() => setSlideOutVisible(false)} />
        ) : null}
        <SearchBarPassengersSelector searchType={searchType} passengers={props.searchPassengers} />
        {props.isCurrentUserAdmin && appContext.isMediaBPNotMobile && (
          <Link
            href={`${API_URL.DEFAULT}/org_admin/organizations/${props.organizationId}/users/new`}
            targetBlank={true}
            type="medium">
            {t('search.bar.passengers.link.addTravellers')}
          </Link>
        )}

        {(props.isCurrentUserAdmin ||
          props.isCurrentUserTravelAssistant ||
          props.isCurrentUserManager) && <SearchBarGuestCreator />}
      </SearchBarSlideOut>
    </div>
  );
};
export default SearchBarPassengers;
